import { useEffect, useState } from 'react';
import { Icon, tokens } from '@/design';
import { auditClient, type AuditClientState } from '@/app/audit/auditDataClient';

export interface AuditRowDetailProps {
  rowId: string;
  onBackToList?: () => void;
  onEvidencePackRequested?: (rowId: string) => void;
}

export function AuditRowDetail({
  rowId,
  onBackToList,
  onEvidencePackRequested,
}: AuditRowDetailProps) {
  const [state, setState] = useState<AuditClientState>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    void auditClient.getAuditRow(rowId).then((result) => {
      if (!cancelled) setState(result);
    });
    return () => {
      cancelled = true;
    };
  }, [rowId]);

  const row = state.kind === 'success' ? state.data.rows[0] : null;

  return (
    <section
      data-gvw-id="GVW-131"
      aria-labelledby="audit-row-detail-heading"
      style={{
        padding: tokens.space[4],
        background: tokens.color.surface[0],
        color: tokens.color.ink.primary,
        fontFamily: tokens.font.family.ui,
      }}
    >
      <button type="button" onClick={onBackToList} style={buttonStyle('secondary')}>
        <Icon name="arrow_back" size={16} decorative /> Back to audit rows
      </button>
      <h2 id="audit-row-detail-heading">Audit Row Detail</h2>
      {state.kind === 'loading' ? <Status message="Loading audit row..." /> : null}
      {state.kind === 'backend-not-deployed' ? <Status message="Audit backend is not deployed yet." /> : null}
      {state.kind === 'error' ? <Status message={state.message} /> : null}
      {row ? (
        <>
          <dl
            style={{
              display: 'grid',
              gridTemplateColumns: `minmax(${tokens.layout.popoverSm}px, 1fr) 2fr`,
              gap: tokens.space[2],
              border: tokens.border.thin,
              borderRadius: tokens.radius.md,
              padding: tokens.space[3],
            }}
          >
            <Field label="Row id" value={row.id} />
            <Field label="Actor" value={row.actor} />
            <Field label="Action" value={row.action} />
            <Field label="Target" value={row.target} />
            <Field label="Outcome" value={row.outcome} />
            <Field label="Created" value={new Date(row.createdAt).toLocaleString()} />
            <Field label="Retention until" value={new Date(row.retentionUntil).toLocaleDateString()} />
          </dl>
          <button type="button" onClick={() => onEvidencePackRequested?.(row.id)} style={buttonStyle('primary')}>
            <Icon name="summarize" size={16} decorative /> Build evidence pack
          </button>
        </>
      ) : null}
    </section>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt style={{ color: tokens.color.ink.secondary }}>{label}</dt>
      <dd style={{ margin: 0, fontFamily: tokens.font.family.mono }}>{value}</dd>
    </>
  );
}

function Status({ message }: { message: string }) {
  return <p role="status">{message}</p>;
}

function buttonStyle(kind: 'primary' | 'secondary') {
  return {
    minHeight: tokens.density.touchMin,
    border: tokens.border.thin,
    borderRadius: tokens.radius.input,
    background: kind === 'primary' ? tokens.color.accent.primary : tokens.color.surface[1],
    color: kind === 'primary' ? tokens.color.accent.on : tokens.color.ink.primary,
    paddingInline: tokens.space[3],
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.space[1],
    cursor: 'pointer',
    marginTop: tokens.space[3],
  };
}
